import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../applicationUi/components/Table";
import SOAPForm from "../../applicationUi/components/SOAPForm";
import {
  fetchAppointmentsWithSoapByClientId,
  fetchAppointmentById,
} from "../../store/thunks/appointmentsThunk";
import aiService from "../../utils/aiService";
import heidiService from "../../utils/heidiService";
import { XMarkIcon, SparklesIcon, BeakerIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';

const ClientSoapNotes = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSoapForm, setShowSoapForm] = useState(false);
  
  // AI Summary states
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  
  // Add new state for Heidi
  const [heidiToken, setHeidiToken] = useState(null);
  const [heidiLoading, setHeidiLoading] = useState(false);

  const itemsPerPage = 10;

  const dispatch = useDispatch();
  const client = useSelector((state) => state.clients.selectedClient);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const appointments = useSelector(
    (state) => state.appointments.clientAppointmentsWithSoap || []
  );

  useEffect(() => {
    if (client?._id) {
      setLoading(true);
      dispatch(fetchAppointmentsWithSoapByClientId(client._id))
        .unwrap()
        .catch((error) => {
          console.error("Error fetching appointments:", error);
        })
        .finally(() => setLoading(false));
    }
  }, [client, dispatch]);

  const totalAppointments = appointments.length;
  const totalPages = Math.ceil(totalAppointments / itemsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowClick = async (appointmentId) => {
    console.log(appointmentId);
    try {
      const appointment = await dispatch(
        fetchAppointmentById(appointmentId)
      ).unwrap();
      setSelectedAppointment(appointment);
      setShowSoapForm(true);
    } catch (error) {
      console.error("Error fetching appointment by ID:", error);
    }
  };

  const handleModalClose = () => {
    setSelectedAppointment(null);
    setShowSoapForm(false);
    // Refresh appointments globally through Redux state
    if (client?._id) {
      dispatch(fetchAppointmentsWithSoapByClientId(client._id)).unwrap();
    }
  };

  const handleSummarizeSOAPNotes = async () => {
    // Check if we have appointments data for display purposes
    if (!appointments || appointments.length === 0) {
      toast.warning("No SOAP notes available to summarize.");
      return;
    }

    // Filter appointments that have SOAP notes with content for display count
    const appointmentsWithNotes = appointments.filter((appointment) => {
      // Check if appointment has completed SOAP notes
      const hasCompletedNotes = appointment.soapNoteCompleted || appointment.soapNoteFinalized;
      
      // Check if appointment has soapNote data
      const hasSoapNoteData = appointment.soapNote && 
        (Array.isArray(appointment.soapNote) ? appointment.soapNote.length > 0 : true);
      
      return hasCompletedNotes && hasSoapNoteData;
    });

    if (appointmentsWithNotes.length === 0) {
      toast.warning("No completed SOAP notes found to summarize.");
      return;
    }

    console.log(`Processing ${appointmentsWithNotes.length} SOAP notes for AI summarization using backend processing`);

    setSummaryLoading(true);
    setSummaryError(null);
    setShowSummaryModal(true);

    try {
      // Use the new backend approach with client ID
      const response = await aiService.summarizeSOAPNotesByClientId(
        client._id,
        token
      );
      
      setSummaryResult(response);
    } catch (error) {
      console.error("Error summarizing SOAP notes:", error);
      setSummaryError(error.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false);
    setSummaryResult(null);
    setSummaryError(null);
  };

  const paginatedAppointments = appointments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Function to initialize Heidi with the current client's data
  const initializeHeidi = async () => {
    try {
      setHeidiLoading(true);

      // Get Heidi token if we don't have one
      if (!heidiToken) {
        const token = await heidiService.getHeidiToken();
        setHeidiToken(token);

        // Update the Heidi widget's token
        if (window.Heidi) {
          window.Heidi.updateToken(token);
        }
      }

      if (!window.Heidi) {
        console.error('Heidi widget not loaded');
        return;
      }

      // Get the latest SOAP note data
      const latestAppointment = appointments[0];
      if (!latestAppointment?.soapNote?.length) {
        toast.warning('No SOAP notes available to analyze');
        return;
      }

      const latestNote = latestAppointment.soapNote[latestAppointment.soapNote.length - 1];
      let noteData;

      if (latestNote.templateUsed === 'structured') {
        noteData = latestNote.noteData;
      } else {
        // For freeform notes, convert to structured format
        noteData = {
          subjective: Object.entries(latestNote.noteData)
            .filter(([key]) => key.toLowerCase().includes('subjective'))
            .map(([key, value]) => value)
            .join('\n'),
          objective: Object.entries(latestNote.noteData)
            .filter(([key]) => key.toLowerCase().includes('objective'))
            .map(([key, value]) => value)
            .join('\n'),
          assessment: Object.entries(latestNote.noteData)
            .filter(([key]) => key.toLowerCase().includes('assessment'))
            .map(([key, value]) => value)
            .join('\n'),
          plan: Object.entries(latestNote.noteData)
            .filter(([key]) => key.toLowerCase().includes('plan'))
            .map(([key, value]) => value)
            .join('\n'),
        };
      }

      // Update Heidi options with the current client's data
      const heidiData = {
        content: `
          Patient Name: ${client?.firstName} ${client?.lastName}
          Date: ${new Date(latestAppointment.start).toLocaleDateString()}
          
          Subjective: ${noteData.subjective || ''}
          
          Objective: ${noteData.objective || ''}
          
          Assessment: ${noteData.assessment || ''}
          
          Plan: ${noteData.plan || ''}
        `,
        responses: [],
      };

      // Set up data handler
      window.Heidi.onPushData(async (data) => {
        try {
          // Save the analysis results
          await heidiService.saveHeidiAnalysis(
            client._id,
            latestAppointment._id,
            data
          );
          console.log('Heidi analysis saved successfully');
        } catch (error) {
          console.error('Error saving Heidi analysis:', error);
        }
      });

      // Open Heidi with the template data
      window.Heidi.open({ template: heidiData });
    } catch (error) {
      console.error('Error initializing Heidi:', error);
      toast.error('Error initializing Heidi Health. Please try again.');
    } finally {
      setHeidiLoading(false);
    }
  };

  const renderSummaryModal = () => {
    if (!showSummaryModal) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full m-4 max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <SparklesIcon className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                AI SOAP Notes Summary
              </h3>
              <div className="text-sm text-gray-500">
                for {client?.firstName} {client?.lastName}
              </div>
            </div>
            <button
              onClick={handleCloseSummaryModal}
              className="rounded-sm p-2 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {summaryLoading && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600">
                    Analyzing {appointments.filter(apt => {
                      const hasCompletedNotes = apt.soapNoteCompleted || apt.soapNoteFinalized;
                      const hasSoapNoteData = apt.soapNote && (Array.isArray(apt.soapNote) ? apt.soapNote.length > 0 : true);
                      return hasCompletedNotes && hasSoapNoteData;
                    }).length} SOAP notes and generating summary...
                  </span>
                </div>
                <div className="text-sm text-gray-500 text-center max-w-lg">
                  <strong>Please wait:</strong> AI processing can take 3-8 minutes depending on the amount of data. 
                  The system is fetching completed SOAP notes from the database and using advanced language models to provide comprehensive clinical insights.
                  <br /><br />
                  <em>Please keep this window open during processing.</em>
                  <br /><br />
                  <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                    <strong>Note:</strong> Due to server timeout limits, very large requests may timeout even if the AI completes processing. 
                    If you encounter timeouts, try processing fewer SOAP notes at once.
                  </div>
                </div>
              </div>
            )}

            {summaryError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="text-red-600 font-medium">Processing Error</div>
                </div>
                <div className="text-red-700 mb-3">{summaryError}</div>
                
                {summaryError.includes('timed out') && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                    <div className="text-blue-800 text-sm">
                      <strong>Troubleshooting Tips:</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>The AI server may have completed processing - try the request again</li>
                        <li>Consider processing fewer SOAP notes at once (e.g., most recent 10-15 notes)</li>
                        <li>Wait a few minutes before retrying to avoid server overload</li>
                        <li>Contact support if timeouts persist with smaller datasets</li>
                      </ul>
                    </div>
                  </div>
                )}
                
                <div className="mt-3">
                  <button
                    onClick={() => {
                      setSummaryError(null);
                      setShowSummaryModal(false);
                    }}
                    className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {summaryResult && !summaryLoading && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <SparklesIcon className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-blue-900">
                      AI-Generated Summary
                    </span>
                  </div>
                  <div className="text-sm text-blue-800">
                    Based on {appointments.filter(apt => apt.soapNote && apt.soapNote.length > 0).length} SOAP notes
                  </div>
                </div>

                <div className="prose max-w-none">
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                      {summaryResult.response || summaryResult.message || JSON.stringify(summaryResult, null, 2)}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-xs text-gray-500">
                    <strong>Note:</strong> This summary is generated by AI and should be reviewed by a qualified healthcare professional. 
                    It is intended to assist with clinical documentation and should not replace professional clinical judgment.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Add button to action buttons section
  const renderActionButtons = () => (
    <div className="flex justify-between items-center">
      <div></div>
      <div className="flex space-x-2">
        <button
          onClick={handleSummarizeSOAPNotes}
          disabled={summaryLoading || !appointments || appointments.length === 0}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-sm shadow-sm transition-colors ${
            summaryLoading || !appointments || appointments.length === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }`}
        >
          <SparklesIcon className="h-4 w-4 mr-2" />
          {summaryLoading ? 'Processing (3-8 min)...' : 'Summarize SOAP Notes with AI'}
        </button>

        <button
          onClick={initializeHeidi}
          disabled={heidiLoading || !appointments || appointments.length === 0}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-sm shadow-sm transition-colors ${
            heidiLoading || !appointments || appointments.length === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-purple-600 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
          }`}
        >
          <BeakerIcon className="h-4 w-4 mr-2" />
          {heidiLoading ? 'Initializing Heidi...' : 'Analyze with Heidi Health'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div data-component="ClientSoapNotes" className="space-y-4">
        {renderActionButtons()}
        
        {/* Table */}
        {loading ? (
          <div className="p-4 text-gray-600">Loading appointments...</div>
        ) : (
          <Table
            columns={[
              { header: "Title", accessor: "title" },
              {
                header: "Service Date",
                accessor: (row) => {
                  const date = new Date(row.start);
                  return date.toLocaleDateString("en-US", {
                    weekday: 'short',
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                },
              },
              {
                header: "Note Completed",
                accessor: (row) => (row.soapNoteCompleted ? "Yes" : "No"),
              },
              {
                header: "Note Finalized (Approved)",
                accessor: (row) => (row.soapNoteFinalized ? "Yes" : "No"),
              },
            ]}
            data={paginatedAppointments}
            title="Client SOAP Notes"
            description={`List of saved, completed, and finalized (approved) SOAP Notes for ${client?.firstName} ${client?.lastName}`}
            showPagination={true}
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={totalAppointments}
            onPageChange={handlePageChange}
            onRowClick={(item) => handleRowClick(item)} // Pass the appointment ID
            actions={[
              {
                label: "View",
                onClick: (item) => handleRowClick(item), // Pass the appointment ID
              },
            ]}
          />
        )}
      </div>

      {/* SOAP Form Modal */}
      {showSoapForm && selectedAppointment && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="relative bg-white rounded-sm shadow-xl max-w-7xl w-full m-4">
            <SOAPForm
              appointment={selectedAppointment} // Pass the full appointment object
              onClose={handleModalClose}
            />
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {renderSummaryModal()}
    </>
  );
};

export default ClientSoapNotes;
